import { GetServerSideProps } from 'next'
import { NextSeo } from 'next-seo'
import { Suspense, useEffect, useState } from 'react'
import { WafFriendlyPass } from 'src/types/waf-friendly-pass'
import { Meta } from 'src/utils/meta'
import { wpApiUrl } from 'src/utils/wpApiUrl'

import { SplitSmallStack } from 'src/components/splitContainer/splitSmallStack'
import { SpotTypeLink } from 'src/components/spot/typeLink'
import { SearchQuery } from 'src/components/searchQuery'
import { SplitLarge } from 'src/components/splitContainer/splitLarge'
import { SplitSmall } from 'src/components/splitContainer/splitSmall'
import { Container } from 'src/components/container'
import { SwitchLink } from 'src/components/switchLink'
import Device from 'src/components/device'

import { SearchQueryMobileFilterTriggerButton } from 'src/components/searchQuery/mobileFilterTriggerButton'
import { ServiceCardList } from 'src/components/service/cardList'
import { CarouselServicePickup } from 'src/components/caroucel/servicePickup'
import { EmptyPostView } from 'src/components/emptyPostView'
import { useRouter } from 'next/router'
import { SkeletonLoader } from 'src/components/skeleton'
import { fetchWithCache } from 'src/utils/fetchWithCache'

import styles from './styles.module.scss'
import { isStaging } from 'src/utils/isStaging'
import { sleep } from 'src/utils/sleep'
import dynamic from 'next/dynamic'
import { useFetchAllPosts } from 'src/hooks/useFetchAllPosts'

const DynamicSplitContainer = dynamic(
  () => import('src/components/splitContainer/splitContainer'),
  { loading: () => <p>Now Loading...</p> }
)
const DynamicPagination = dynamic(() => import('src/components/pagination'), {
  suspense: true,
})
const DynamicServiceCard = dynamic(
  () => import('src/components/service/card'),
  {
    suspense: true,
  }
)

type Props = {
  total: {
    posts: string | number
    pages: string | number
  }
  serviceList: WafFriendlyPass.service[]
  spotTypeList: WafFriendlyPass.taxonomy[]
  spotAreaList: WafFriendlyPass.taxonomy[]
  spotFeatureList: WafFriendlyPass.taxonomy[]
  currentPageNumber: number
  areaId: number
  featureId: number
  typeId: number
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const currentPageNumber = Number(context?.params?.page)
  const areaId = Number(context?.query?.area)
  const featureId = Number(context?.query?.spot_feature)
  const typeId = Number(context?.query?.spot_type)
  return {
    props: {
      currentPageNumber,
      areaId,
      featureId,
      typeId,
    },
  }
}

const ServicePage = ({
  currentPageNumber,
  areaId,
  featureId,
  typeId,
}: Props) => {
  const router = useRouter()
  const [loadOnce, setLoadOnce] = useState(false)
  const [loading, setLoading] = useState(false)
  const [queryFlag, setQueryFlag] = useState(false)
  const [filterSubmitState, setFilterSubmitState] = useState(false)
  const [toggle, setToggle] = useState(false)
  const [services, setServices] = useState<WafFriendlyPass.service[]>([])
  const [spotTypes, setSpotTypes] = useState<WafFriendlyPass.taxonomy[]>([])
  const [spotAreas, setSpotAreas] = useState<WafFriendlyPass.taxonomy[]>([])
  const [spotFeatures, setSpotFeatures] = useState<WafFriendlyPass.taxonomy[]>(
    []
  )
  const [total, setTotal] = useState<WafFriendlyPass.total>()
  const limit = 12

  const getPostsSet = async () => {
    setLoading(true)
    const fetchUrl = new URL(
      `${wpApiUrl.service}?page=${currentPageNumber}&per_page=${limit}&_fields=id,acf,spot_feature, area, type`
    )
    const urlSearchParams = new URLSearchParams()
    setSpotAreas(
      (await fetchWithCache(
        `${wpApiUrl.taxonomyArea}`
      )) as Props['spotAreaList']
    )
    setSpotFeatures(
      (await fetchWithCache(
        `${wpApiUrl.taxonomySpotFeature}`
      )) as Props['spotFeatureList']
    )
    setSpotTypes(
      (await fetchWithCache(
        `${wpApiUrl.taxonomySpotType}`
      )) as Props['spotTypeList']
    )

    let serviceFetch

    const appendParams = async (query: string, id: string) => {
      urlSearchParams.append(query, `${id}`)
      fetchUrl.searchParams.append(query, `${id}`)
    }

    if (areaId || featureId || typeId) {
      setQueryFlag(true)
      if (areaId) {
        appendParams('area', String(areaId))
        serviceFetch = await fetch(String(fetchUrl))
      }
      if (featureId) {
        appendParams('spot_feature', String(featureId))
        serviceFetch = await fetch(String(fetchUrl))
      }
      if (typeId) {
        appendParams('spot_type', String(typeId))
        serviceFetch = await fetch(String(fetchUrl))
      }
    } else {
      serviceFetch = await fetch(fetchUrl.toString())
    }
    const headers = serviceFetch?.headers
    const total = {
      posts: headers?.get('X-WP-Total'),
      pages: headers?.get('X-WP-TotalPages'),
    }
    const serviceList = (await serviceFetch?.json()) as Props['serviceList']
    setTotal(total)
    setServices(
      isStaging ? serviceList : serviceList.filter((item) => item.acf.status)
    )
    setLoading(false)
  }

  useEffect(() => {
    if (router.isReady) {
      if (loadOnce) {
        return
      }
      if (!filterSubmitState) {
        getPostsSet()
        setLoadOnce(true)
      }
    }
  }, [])

  const updateView = () => {
    if (!filterSubmitState) {
      return
    }
    if (loading) {
      sleep(500).then(() => {
        router.reload()
        return sleep(500)
      })
    }
  }

  useEffect(() => {
    router.events.on('routeChangeComplete', updateView)
    return () => {
      router.events.off('routeChangeComplete', updateView)
    }
  }, [filterSubmitState, loading])

  const { allPosts } = useFetchAllPosts(`${wpApiUrl.allPosts}/service`)
  const pickupPosts = allPosts?.filter((post: WafFriendlyPass.service) => post.acf.pickup)

  return (
    <div>
      <NextSeo
        title={`特典から探す | ${Meta.TITLE}`}
        openGraph={{
          url: `https://pass.workation-fukuoka.jp/service/page/1`,
          title: `特典から探す | ${Meta.TITLE}`,
        }}
      />
      <Container>
        <div className={styles.service}>
          <h1 className={styles.serviceHeading}>
            {!loading && queryFlag && (
              <span>
                「
                <small>
                  {spotTypes.find((item) => item.id == String(typeId))?.name}
                </small>
                <small>
                  {spotAreas.find((item) => item.id == String(areaId))?.name}
                </small>
                <small>
                  {
                    spotFeatures.find((item) => item.id == String(featureId))
                      ?.name
                  }
                </small>
                」の
              </span>
            )}
            <span>ワフパス特典</span>
          </h1>
          <div className={styles.serviceSwitchLink}>
            <SwitchLink />
          </div>
          {currentPageNumber === 1 && (
            <div className={styles.servicePickup}>
              <h2 className={styles.serviceSubHeading}>
                ユーザーから<span>人気</span>の特典
              </h2>
              <CarouselServicePickup
                items={pickupPosts}
              />
            </div>
          )}
          <div className={styles.serviceSplit}>
            <DynamicSplitContainer>
              <SplitSmall>
                <SplitSmallStack heading="カテゴリから探す">
                  <button
                    className={styles.serviceTypeLink}
                    onClick={async () => {
                      setLoading(true)
                      setFilterSubmitState(true)
                      router.push('/service/page/1')
                    }}
                  >
                    <span className={styles.serviceTypeLinkIcon}>
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 40 40"
                        fill="none"
                      >
                        <rect
                          x="0.5"
                          y="0.5"
                          width="39"
                          height="39"
                          rx="3.5"
                          fill="#F4F5F8"
                        />
                        <path
                          d="M7 24.8L10.878 15H12.894L16.744 24.8H14.336L12.488 19.83C12.4133 19.634 12.3387 19.424 12.264 19.2C12.1893 18.976 12.1147 18.7473 12.04 18.514C11.9653 18.2713 11.8907 18.038 11.816 17.814C11.7507 17.5807 11.6947 17.366 11.648 17.17L12.068 17.156C12.012 17.3893 11.9467 17.618 11.872 17.842C11.8067 18.066 11.7367 18.29 11.662 18.514C11.5967 18.7287 11.522 18.948 11.438 19.172C11.354 19.3867 11.2747 19.6107 11.2 19.844L9.352 24.8H7ZM8.848 22.924L9.59 21.132H14.098L14.826 22.924H8.848Z"
                          fill="#31A8DE"
                        />
                        <path
                          d="M17.9667 24.8V15H20.3467V22.7H24.7147V24.8H17.9667Z"
                          fill="#31A8DE"
                        />
                        <path
                          d="M26.498 24.8V15H28.878V22.7H33.246V24.8H26.498Z"
                          fill="#31A8DE"
                        />
                      </svg>
                    </span>
                    <span className={styles.serviceTypeLinkValue}>全て</span>
                  </button>
                  {spotTypes.map((spotType, idx) => (
                    <SpotTypeLink
                      setState={(state: boolean) => setLoading(state)}
                      setFilterSubmitState={(state: boolean) =>
                        setFilterSubmitState(state)
                      }
                      key={idx}
                      spotType={spotType}
                      pathname="service"
                    />
                  ))}
                </SplitSmallStack>
                <Device>
                  {({ isBrowser }) => {
                    if (isBrowser) {
                      return (
                        <SplitSmallStack heading="条件を絞り込む">
                          <SearchQuery
                            pathname={`/service/page/1`}
                            areas={spotAreas}
                            features={spotFeatures}
                            setState={(state: boolean) => setLoading(state)}
                            setFilterSubmitState={(state: boolean) =>
                              setFilterSubmitState(state)
                            }
                          />
                        </SplitSmallStack>
                      )
                    } else {
                      return (
                        <SplitSmallStack column>
                          <SearchQueryMobileFilterTriggerButton
                            state={toggle}
                            setState={(state: boolean) => setToggle(state)}
                          />
                          {toggle ? (
                            <SearchQuery
                              pathname={`/service/page/1`}
                              areas={spotAreas}
                              features={spotFeatures}
                              setState={(state: boolean) => setLoading(state)}
                              setFilterSubmitState={(state: boolean) =>
                                setFilterSubmitState(state)
                              }
                            />
                          ) : (
                            false
                          )}
                        </SplitSmallStack>
                      )
                    }
                  }}
                </Device>
              </SplitSmall>
              <SplitLarge>
                {!loading ? (
                  <>
                    {services && (
                      <ServiceCardList>
                        <Suspense fallback={<SkeletonLoader count={4} />}>
                          {services.map((service, idx) => {
                            return (
                              <DynamicServiceCard service={service} key={idx} />
                            )
                          })}
                        </Suspense>
                      </ServiceCardList>
                    )}
                    {!services.length && <EmptyPostView />}
                  </>
                ) : (
                  <SkeletonLoader count={4} />
                )}
              </SplitLarge>
            </DynamicSplitContainer>
          </div>
          {Number(total?.posts) > limit && (
            <div className={styles.servicePagination}>
              <Suspense fallback={false}>
                <DynamicPagination
                  total={Number(total?.posts)}
                  pages={Number(total?.pages)}
                  pathname={`/service/page/`}
                  currentPageNumber={currentPageNumber}
                  setState={(state: boolean) => setLoading(state)}
                  setFilterSubmitState={(state: boolean) =>
                    setFilterSubmitState(state)
                  }
                />
              </Suspense>
            </div>
          )}
        </div>
      </Container>
    </div>
  )
}

export default ServicePage
